import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import 'chart.js/auto';
import { Chart } from 'primereact/chart';
import { useGetAnalytics } from '../../queries/marketing';

// Resolve a CSS custom property from :root. Chart.js can't consume var(--*)
// directly — same pattern as AnalyticsDetailDialog.jsx.
const cssVar = (name, fallback) => {
  if (typeof window === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
};

const SOURCE_COLORS = {
  birthday_event: '--blue-400',
  spin_win:       '--purple-400',
  happy_hour:     '--orange-400',
  manual:         '--teal-400',
  import:         '--cyan-400',
};

/**
 * Smart CRM Step 10 — three analytics charts:
 *   1. Customer growth (line/area, last 6 months)
 *   2. Customers by capture source (doughnut)
 *   3. Campaign funnel trend (grouped bar, last 8 weeks)
 *
 * Uses Chart.js 4 via PrimeReact's Chart wrapper, matching the existing
 * AnalyticsDetailDialog pattern. Resolves theme tokens via cssVar().
 */
export default function AnalyticsChartsPanel() {
  const { t } = useTranslation();
  const { data, isLoading } = useGetAnalytics();

  const growth  = data?.growth ?? [];
  const sources = data?.sources ?? {};
  const funnel  = data?.funnel ?? [];

  const growthData = useMemo(() => ({
    labels: growth.map((r) => r.period),
    datasets: [
      {
        label:           t('marketing.chart_new_customers'),
        data:            growth.map((r) => r.new),
        backgroundColor: cssVar('--blue-100', '#dbeafe') + 'cc',
        borderColor:     cssVar('--blue-500', '#3b82f6'),
        fill:            true,
        tension:         0.3,
        pointRadius:     3,
      },
    ],
  }), [growth, t]);

  const growthOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  }), []);

  const sourceLabels = Object.keys(sources);
  const sourceData = useMemo(() => ({
    labels: sourceLabels.map((s) => t(`marketing.source_${s}`)),
    datasets: [{
      data:            sourceLabels.map((s) => sources[s]),
      backgroundColor: sourceLabels.map((s) => cssVar(SOURCE_COLORS[s] ?? '--surface-400', '#94a3b8')),
      borderWidth:     1,
      borderColor:     cssVar('--surface-border', '#e2e8f0'),
    }],
  }), [sources, sourceLabels, t]);

  const sourceOptions = useMemo(() => ({
    responsive:          true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12 } },
    },
  }), []);

  const funnelData = useMemo(() => {
    const labels = funnel.map((r) => r.week_start);
    const mk = (key, cssToken, fallback) => ({
      label:           t(`marketing.sum_${key}`),
      data:            funnel.map((r) => r[key]),
      backgroundColor: cssVar(cssToken, fallback),
      borderRadius:    3,
    });
    return {
      labels,
      datasets: [
        mk('sent',      '--blue-400',   '#60a5fa'),
        mk('delivered', '--green-400',  '#4ade80'),
        mk('opened',    '--teal-400',   '#2dd4bf'),
        mk('clicked',   '--purple-400', '#c084fc'),
      ],
    };
  }, [funnel, t]);

  const funnelOptions = useMemo(() => ({
    responsive:          true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom', labels: { boxWidth: 12 } } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
  }), []);

  if (isLoading) {
    return (
      <div className="flex gap-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="surface-card border-round p-3 flex-1" style={{ minHeight: 220 }}>
            <div className="text-400 text-center pt-5">{t('marketing.loading')}</div>
          </div>
        ))}
      </div>
    );
  }

  const hasGrowth  = growth.some((r) => r.new > 0);
  const hasSources = Object.values(sources).some((v) => v > 0);
  const hasFunnel  = funnel.some((r) => r.sent > 0);

  return (
    <div>
      <h3 className="mt-0 mb-3">{t('marketing.tab_analytics')}</h3>
      <div className="grid">
        {/* Customer growth */}
        <div className="col-12 md:col-8">
          <div className="surface-card border-round p-3">
            <div className="font-semibold mb-3">{t('marketing.chart_growth_title')}</div>
            {hasGrowth ? (
              <div style={{ height: 200 }}>
                <Chart type="line" data={growthData} options={growthOptions} style={{ height: '100%' }} />
              </div>
            ) : (
              <div className="text-400 text-sm py-5 text-center">{t('marketing.chart_no_data')}</div>
            )}
          </div>
        </div>

        {/* By source */}
        <div className="col-12 md:col-4">
          <div className="surface-card border-round p-3">
            <div className="font-semibold mb-3">{t('marketing.chart_sources_title')}</div>
            {hasSources ? (
              <div style={{ height: 200 }}>
                <Chart type="doughnut" data={sourceData} options={sourceOptions} style={{ height: '100%' }} />
              </div>
            ) : (
              <div className="text-400 text-sm py-5 text-center">{t('marketing.chart_no_data')}</div>
            )}
          </div>
        </div>

        {/* Funnel trend */}
        <div className="col-12">
          <div className="surface-card border-round p-3">
            <div className="font-semibold mb-3">{t('marketing.chart_funnel_title')}</div>
            {hasFunnel ? (
              <div style={{ height: 220 }}>
                <Chart type="bar" data={funnelData} options={funnelOptions} style={{ height: '100%' }} />
              </div>
            ) : (
              <div className="text-400 text-sm py-5 text-center">{t('marketing.chart_no_data')}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
